// Resolve links found in a document (approved before M4):
// relative paths resolve against the file's own path in the repo;
// .md targets open in Hashquill; other repo files open on GitHub at the PR head;
// "#anchor" scrolls to the heading. Absolute http(s)/mailto links stay external.

export type ResolvedLink =
  | { kind: 'external'; href: string }
  | { kind: 'anchor'; id: string }
  | { kind: 'markdown'; path: string; anchor: string | null }
  | { kind: 'repo-file'; path: string; anchor: string | null }
  | { kind: 'invalid' };

const SCHEME = /^[a-z][a-z\d+.-]*:/i;

/** Repo path of `href` as seen from the file at `fromPath` (both repo-relative). */
export function resolveRepoPath(href: string, fromPath: string): string | null {
  const dir = fromPath.includes('/') ? fromPath.slice(0, fromPath.lastIndexOf('/')) : '';
  const raw = href.startsWith('/') ? href.slice(1) : (dir ? `${dir}/${href}` : href);
  const out: string[] = [];
  for (const seg of raw.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') { if (!out.length) return null; out.pop(); continue; } // above the repo root
    out.push(seg);
  }
  return out.length ? out.join('/') : null;
}

export function resolveLink(href: string, fromPath: string): ResolvedLink {
  const h = href.trim();
  if (!h) return { kind: 'invalid' };
  if (SCHEME.test(h) || h.startsWith('//')) return { kind: 'external', href: h };
  if (h.startsWith('#')) return { kind: 'anchor', id: decode(h.slice(1)) };
  const [pathAndQuery, ...rest] = h.split('#');
  const anchor = rest.length ? decode(rest.join('#')) : null;
  const pathPart = decode(pathAndQuery!.split('?')[0]!);
  const path = resolveRepoPath(pathPart, fromPath);
  if (!path) return { kind: 'invalid' };
  return path.toLowerCase().endsWith('.md') ? { kind: 'markdown', path, anchor } : { kind: 'repo-file', path, anchor };
}

function decode(s: string): string {
  try { return decodeURIComponent(s); } catch { return s; }
}

/** GitHub URL for a repo file at a commit. */
export function githubBlobUrl(repoFull: string, sha: string, path: string, anchor: string | null): string {
  const p = path.split('/').map(encodeURIComponent).join('/');
  return `https://github.com/${repoFull}/blob/${sha}/${p}${anchor ? `#${encodeURIComponent(anchor)}` : ''}`;
}

/** Element id of a heading anchor after the sanitizer's clobber prefix. */
export const anchorElementId = (id: string) => (id.startsWith('user-content-') ? id : `user-content-${id.toLowerCase()}`);
